import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../../../../components/ui/button";
import { X, Package, Building2, MapPin, CreditCard, FileText, Hash, Download, Mail, Plus, Trash2 } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { sendGenericEmail } from "../../../../../../ApiCalls/mantisEmail";

const PurchaseOrderDetailsModal = ({
  open,
  onClose,
  purchaseOrder,
  purchaseOrderItems,
  mantisBusiness,
  data,
}) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  // Initialize recipient emails with supplier email when modal opens
  React.useEffect(() => {
    if (open && purchaseOrder?.supplier_email) {
      setRecipientEmails([purchaseOrder.supplier_email]);
      setEmailSubject(
        `Orden de Compra ${
          purchaseOrder?.po_number ||
          purchaseOrder?.friendly_id ||
          `PO-${purchaseOrder?.id}`
        }`
      );
      setEmailMessage(
        `
Adjunto encontrará la Orden de Compra ${
          purchaseOrder?.po_number ||
          purchaseOrder?.friendly_id ||
          `PO-${purchaseOrder?.id}`
        }.

Detalles de la orden:
- Fecha: ${
          purchaseOrder?.created_at
            ? new Date(purchaseOrder.created_at).toLocaleDateString()
            : "N/A"
        }
- Empresa: ${mantisBusiness?.name || "N/A"}
          - Total: ${purchaseOrder?.currency || "USD"} ${(
          purchaseOrder?.total_amount || 0
        ).toLocaleString("es-CO", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}

Saludos cordiales,
${mantisBusiness?.name || "Su empresa"}
      `.trim()
      );
    }
  }, [open, purchaseOrder?.supplier_email, purchaseOrder, mantisBusiness]);

  if (!open || !purchaseOrder) return null;

  const addEmail = () => {
    if (newEmail && !recipientEmails.includes(newEmail)) {
      setRecipientEmails([...recipientEmails, newEmail]);
      setNewEmail("");
    }
  };

  const removeEmail = (emailToRemove) => {
    setRecipientEmails(
      recipientEmails.filter((email) => email !== emailToRemove)
    );
  };

  const sendEmail = async () => {
    if (recipientEmails.length === 0) return;

    setIsSendingEmail(true);
    try {
      // Generate PDF first
      const modalContent = document.getElementById("purchase-order-content");
      if (!modalContent) return;

      const canvas = await html2canvas(modalContent, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Convert PDF to base64
      const pdfBase64 = pdf.output("datauristring").split(",")[1];

      const fileName = `PurchaseOrder_${
        purchaseOrder?.po_number ||
        purchaseOrder?.friendly_id ||
        purchaseOrder?.id ||
        "PO"
      }.pdf`;

      const emailData = {
        toEmails: recipientEmails,
        subject:
          emailSubject ||
          `Orden de Compra ${
            purchaseOrder?.po_number ||
            purchaseOrder?.friendly_id ||
            `PO-${purchaseOrder?.id}`
          }`,
        message:
          emailMessage ||
          `
          Adjunto encontrará la Orden de Compra ${
            purchaseOrder?.po_number ||
            purchaseOrder?.friendly_id ||
            `PO-${purchaseOrder?.id}`
          }.
          
          Detalles de la orden:
          - Fecha: ${
            purchaseOrder?.created_at
              ? new Date(purchaseOrder.created_at).toLocaleDateString()
              : "N/A"
          }
          - Empresa: ${mantisBusiness?.name || "N/A"}
          - Total: ${purchaseOrder?.currency || "USD"} ${(
            purchaseOrder?.total_amount || 0
          ).toLocaleString("es-CO", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          
          Saludos cordiales,
          ${mantisBusiness?.name || "Su empresa"}
        `,
        attachments: [
          {
            filename: fileName,
            base64: pdfBase64,
            mimeType: "application/pdf",
          },
        ],
      };

      await sendGenericEmail(emailData);
      setShowEmailModal(false);
      alert("Orden de compra enviada exitosamente");
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Error al enviar el email: " + error.message);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const downloadPDF = async () => {
    const modalContent = document.getElementById("purchase-order-content");
    if (!modalContent) return;

    try {
      const canvas = await html2canvas(modalContent, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `PurchaseOrder_${
        purchaseOrder?.po_number ||
        purchaseOrder?.friendly_id ||
        purchaseOrder?.id ||
        "PO"
      }.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-orange-100 dark:bg-orange-800 rounded flex items-center justify-center">
                <FileText className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Detalles de la Orden de Compra
              </h3>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEmailModal(true)}
                className="flex items-center space-x-2"
              >
                <Mail className="h-4 w-4" />
                <span>Enviar por Email</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadPDF}
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Descargar PDF</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div
              id="purchase-order-content"
              className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto"
            >
              {/* Purchase Order Header */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-orange-700 mb-2">
                  Orden de Compra
                </h1>
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <div>
                    Fecha:{" "}
                    {purchaseOrder?.created_at
                      ? new Date(purchaseOrder.created_at).toLocaleDateString()
                      : data?.created_at
                      ? new Date(data.created_at).toLocaleDateString()
                      : "N/A"}
                  </div>
                  <div>
                    Número de Orden:{" "}
                    {purchaseOrder?.po_number ||
                      purchaseOrder?.friendly_id ||
                      `PO-${purchaseOrder?.id}`}
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Company Details */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2 flex items-center">
                    <Building2 className="h-4 w-4 mr-2" />
                    Información de la Empresa
                  </h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    {mantisBusiness?.name && <div>{mantisBusiness.name}</div>}
                    {purchaseOrder?.address && (
                      <div>{purchaseOrder.address}</div>
                    )}
                    {data?.address_line_2 && <div>{data.address_line_2}</div>}
                    {data?.phone && <div>{data.phone}</div>}
                    {mantisBusiness?.contactEmail && (
                      <div>{mantisBusiness.contactEmail}</div>
                    )}
                    {mantisBusiness?.website && (
                      <div>{mantisBusiness.website}</div>
                    )}
                  </div>
                </div>

                {/* Vendor and Ship To */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Vendor */}
                  <div>
                    <div className="bg-orange-600 text-white text-center py-2 px-3 text-sm font-bold mb-2">
                      VENDEDOR
                    </div>
                    <div className="text-sm text-gray-600 space-y-1 break-words">
                      {purchaseOrder?.supplier_name && (
                        <div className="break-words">
                          {purchaseOrder.supplier_name}
                        </div>
                      )}
                      {purchaseOrder?.supplier_nit && (
                        <div className="break-words">
                          NIT: {purchaseOrder.supplier_nit}
                        </div>
                      )}
                      {purchaseOrder?.supplier_address && (
                        <div className="break-words">
                          {purchaseOrder.supplier_address}
                        </div>
                      )}
                      {purchaseOrder?.supplier_phone && (
                        <div className="break-words">
                          Tel: {purchaseOrder.supplier_phone}
                        </div>
                      )}
                      {purchaseOrder?.supplier_email && (
                        <div className="break-words">
                          Email: {purchaseOrder.supplier_email}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ship To */}
                  <div>
                    <div className="bg-orange-600 text-white text-center py-2 px-3 text-sm font-bold mb-2">
                      ENVIAR A
                    </div>
                    <div className="text-sm text-gray-600 space-y-1 break-words">
                      {purchaseOrder?.shipping_address && (
                        <div className="break-words">
                          {purchaseOrder.shipping_address}
                        </div>
                      )}
                      {purchaseOrder?.city && (
                        <div className="break-words">{purchaseOrder.city}</div>
                      )}
                      {purchaseOrder?.zip_code && (
                        <div className="break-words">
                          {purchaseOrder.zip_code}
                        </div>
                      )}
                      {mantisBusiness?.name && (
                        <div className="break-words font-medium">
                          {mantisBusiness.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Details */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div>
                  <div className="bg-orange-600 text-white text-center py-2 px-3 text-sm font-bold mb-2">
                    TÉRMINOS DE ENVÍO
                  </div>
                  <div className="text-sm text-gray-600 text-center">
                    {purchaseOrder?.shipping_terms || "N/A"}
                  </div>
                </div>
                <div>
                  <div className="bg-orange-600 text-white text-center py-2 px-3 text-sm font-bold mb-2">
                    TÉRMINOS DE PAGO
                  </div>
                  <div className="text-sm text-gray-600 text-center">
                    {purchaseOrder?.payment_terms || "N/A"}
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="mb-8">
                {!purchaseOrderItems || purchaseOrderItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">
                      No hay artículos disponibles
                    </p>
                    <p className="text-sm">
                      Los artículos de la orden de compra se cargarán
                      automáticamente
                    </p>
                  </div>
                ) : (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-orange-600 text-white">
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          ITEM #
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          DESCRIPCIÓN
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-center">
                          CANTIDAD
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-center">
                          UNIDAD
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-right">
                          PRECIO UNITARIO
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-right">
                          TOTAL
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseOrderItems.map((item, index) => (
                        <tr
                          key={item.id || index}
                          className="border-b border-gray-300"
                        >
                          <td className="border border-gray-300 px-4 py-2 text-sm">
                            {String(index + 1).padStart(5, "0")}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm">
                            {item.name || "[Description]"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-center">
                            {item.quantity || 1}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-center">
                            {item.unit || "Unidad"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-right">
                            {purchaseOrder?.currency || "USD"}{" "}
                            {(parseFloat(item.unit_price) || 0).toLocaleString(
                              "es-CO",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-right">
                            {purchaseOrder?.currency || "USD"}{" "}
                            {(parseFloat(item.total_price) || 0).toLocaleString(
                              "es-CO",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Special Instructions and Financial Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Special Instructions */}
                <div>
                  <div className="bg-orange-600 text-white text-center py-2 px-3 text-sm font-bold mb-2">
                    NOTAS
                  </div>
                  <div className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
                    {purchaseOrder?.notes || ""}
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="text-right">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>SUBTOTAL:</span>
                      <span className="font-semibold">
                        {purchaseOrder?.currency || "USD"}{" "}
                        {(
                          parseFloat(purchaseOrder?.subtotal_amount) || 0
                        ).toLocaleString("es-CO", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    {purchaseOrder?.discount_percentage && (
                      <div className="flex justify-between">
                        <span>
                          DESCUENTO ({purchaseOrder?.discount_percentage}%):
                        </span>
                        <span className="font-semibold">
                          - {purchaseOrder?.currency || "USD"}{" "}
                          {(
                            (parseFloat(purchaseOrder?.discount_percentage) /
                              100) *
                              parseFloat(purchaseOrder?.subtotal_amount) || 0
                          ).toLocaleString("es-CO", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    )}
                    {purchaseOrder?.tax_rate && (
                      <div className="flex justify-between">
                        <span>
                          TASA DE IMPUESTO ({purchaseOrder?.tax_rate}%):
                        </span>
                        <span className="font-semibold">
                          {purchaseOrder?.currency || "USD"}{" "}
                          {(
                            parseFloat(purchaseOrder?.tax_amount) || 0
                          ).toLocaleString("es-CO", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                        </span>
                      </div>
                    )}
                    {purchaseOrder?.shipping_cost && (
                      <div className="flex justify-between">
                        <span>ENVÍO:</span>
                        <span className="font-semibold">
                          {purchaseOrder?.currency || "USD"}{" "}
                          {(
                            parseFloat(purchaseOrder?.shipping_cost) || 0
                          ).toLocaleString("es-CO", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    )}
                    {purchaseOrder?.other_costs && (
                      <div className="flex justify-between">
                        <span>OTROS COSTOS:</span>
                        <span className="font-semibold">
                          {purchaseOrder?.currency || "USD"}{" "}
                          {(
                            parseFloat(purchaseOrder?.other_costs) || 0
                          ).toLocaleString("es-CO", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between bg-orange-600 text-white px-3 py-2 font-bold">
                      <span>TOTAL:</span>
                      <span>
                        {purchaseOrder?.currency || "USD"}{" "}
                        {(
                          parseFloat(purchaseOrder?.total_amount) || 0
                        ).toLocaleString("es-CO", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Purchase Order Details */}
              <div className="mt-8 pt-6 border-t border-gray-300">
                <h3 className="font-semibold text-gray-700 mb-4 flex items-center">
                  <Hash className="h-4 w-4 mr-2" />
                  Detalles Adicionales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Financial Details */}
                  <div>
                    <h4 className="font-medium text-gray-600 mb-2 flex items-center">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Información Financiera
                    </h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>Moneda: {purchaseOrder?.currency || "USD"}</div>
                      <div>
                        Impuesto: {purchaseOrder?.currency || "USD"}{" "}
                        {(
                          parseFloat(purchaseOrder?.tax_amount) || 0
                        ).toLocaleString("es-CO", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                      <div>
                        Descuento: {purchaseOrder?.discount_percentage || 0}%
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <h4 className="font-medium text-gray-600 mb-2 flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      Ubicación de Entrega
                    </h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      {purchaseOrder?.address && (
                        <div>{purchaseOrder.address}</div>
                      )}
                      {purchaseOrder?.city && <div>{purchaseOrder.city}</div>}
                      {purchaseOrder?.zip_code && (
                        <div>{purchaseOrder.zip_code}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      </div>
      ,{/* Email Modal */}
      {showEmailModal &&
        createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-lg">
              {/* Email Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-orange-100 dark:bg-orange-800 rounded flex items-center justify-center">
                    <Mail className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Enviar Orden de Compra
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowEmailModal(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Email Modal Content */}
              <div className="p-6">
                <div className="space-y-6">
                  {/* Recipients Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Destinatarios:
                    </label>
                    <div className="space-y-3 mb-4">
                      {recipientEmails.map((email, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600"
                        >
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {email}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeEmail(email)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <div className="flex space-x-3">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="Agregar email..."
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-800"
                        onKeyPress={(e) => e.key === "Enter" && addEmail()}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addEmail}
                        className="flex items-center space-x-2 px-4 py-2 border-orange-300 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Agregar</span>
                      </Button>
                    </div>
                  </div>

                  {/* Subject Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Asunto:
                    </label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Asunto del email..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-800"
                    />
                  </div>

                  {/* Message Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Mensaje:
                    </label>
                    <textarea
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      placeholder="Mensaje del email..."
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-800 resize-none"
                    />
                  </div>

                  <div className="text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p>
                      La orden de compra se enviará como PDF adjunto a los
                      destinatarios seleccionados.
                    </p>
                  </div>
                </div>

                {/* Email Modal Footer */}
                <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="outline"
                    onClick={() => setShowEmailModal(false)}
                    className="px-6"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={sendEmail}
                    disabled={recipientEmails.length === 0 || isSendingEmail}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6"
                  >
                    {isSendingEmail ? "Enviando..." : "Enviar Email"}
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>,
    document.body
  );
};

export default PurchaseOrderDetailsModal;
